import type { AssetExposure } from '@/features/home/lib/home-exposure'
import type { TradeIdea } from '@/features/home/lib/home-ideas'

/**
 * The daily brief's AI request and how its answer is read back. The research assistant is asked
 * for fixed markdown headings — "## Brief" for the comment on the datapoints and "## Idea N" for
 * each idea's reasoning — so Home can put each part next to the card it belongs to. When the
 * answer comes back in another shape, the whole text stays the brief. Pure (tested in
 * __tests__/home-brief-prompt.test.ts). English on purpose: it is the assistant's input.
 */

export interface BriefPromptInput {
  exposures: readonly AssetExposure[]
  openOrderCount: number
  /** One line per datapoint, e.g. "BTC options-implied move to Fri 10 Oct: ±3.1% ($2,650)". */
  datapoints: readonly string[]
  /** One line per idea, in display order. */
  ideas: readonly string[]
}

function describeExposure(exposure: AssetExposure): string {
  const positions = exposure.positions
    .map((risk) => {
      const parts = [
        `${risk.side} ${risk.position.symbol}`,
        risk.leverage !== null ? `${risk.leverage}x` : null,
        risk.liquidationDistance !== null
          ? `liquidation ${(risk.liquidationDistance * 100).toFixed(1)}% away`
          : null,
        risk.pnlPercent !== null ? `PnL ${risk.pnlPercent.toFixed(0)}%` : null,
      ].filter(Boolean)
      return parts.join(', ')
    })
    .join('; ')
  const share = Math.round(exposure.share * 100)
  return `- ${exposure.asset} (${share}% of the book): ${positions || 'open orders only'}`
}

export function buildBriefPrompt(input: BriefPromptInput): string {
  const held = input.exposures.filter((exposure) => exposure.positions.length > 0)
  const lines = [
    'Write my daily trading brief. Be concrete and beginner-friendly, no hype, no generic advice.',
    'Keep the whole answer under 260 words. No tables.',
    'Use exactly these markdown headings and nothing before the first one:',
    '## What changed',
    'One or two sentences: what moved in the datapoints below since yesterday.',
    '## What it means for you',
    'One or two sentences on my positions specifically (liquidation, funding, size).',
    '## What to watch',
    'One sentence: the level or event that matters next.',
    '## Cards',
    'One line per datapoint below, starting with its label ("D1: …"): what changed in it and what that does to my positions. Skip a datapoint with nothing worth saying.',
    ...input.ideas.map(
      (_, index) =>
        `## Idea ${index + 1}\n2–3 sentences: is idea ${index + 1} sensible right now, and what to watch.`
    ),
    '',
    held.length > 0 ? 'My positions:' : 'I have no open positions.',
    ...held.map(describeExposure),
    `Open orders: ${input.openOrderCount}.`,
    '',
    'Datapoints:',
    ...input.datapoints.map((line, index) => `D${index + 1}. ${line}`),
  ]
  if (input.ideas.length > 0) {
    lines.push('', 'Ideas:', ...input.ideas.map((line, index) => `${index + 1}. ${line}`))
  }
  return lines.join('\n')
}

export interface BriefSections {
  changed: string
  meaning: string
  watch: string
}

export interface ParsedBrief {
  /** A few sentences for the brief panel. */
  brief: string
  /** The fixed skeleton, when the answer kept to it. */
  sections: BriefSections | null
  /** Reasoning per idea, by 1-based idea number, when the answer used the "## Idea N" headings. */
  ideas: Record<number, string>
  /** One line per datapoint card, by 1-based datapoint number ("D3: …" under "## Cards"). */
  cardNotes: Record<number, string>
  /** The whole answer, for "Read the full brief". */
  full: string
  /** Whether the answer followed the requested headings. */
  structured: boolean
}

const HEADING =
  /^#{1,4}\s*(brief|what changed|what it means for you|what to watch|cards|idea\s*(\d+))\b[^\n]*$/gim
/** A card note line: "D3: …", "- **D3** — …", "[D3] …". */
const CARD_NOTE = /^\s*(?:[-*]\s*)?(?:\*\*)?\[?D(\d+)\]?(?:\*\*)?\s*[:.—–-]\s*(.+)$/i

export function parseCardNotes(body: string): Record<number, string> {
  const notes: Record<number, string> = {}
  for (const line of body.split('\n')) {
    const match = CARD_NOTE.exec(line)
    if (match) notes[Number(match[1])] = match[2].replace(/\*\*/g, '').trim()
  }
  return notes
}
/** Research citations: "[5]", "[13, 17]", "[19–20]". */
const CITATION = /\s?\[\d+(?:\s*[,–-]\s*\d+)*\]/g
/** Words that make a paragraph about the user's book rather than the market in general. */
const RELEVANT = ['liquidation', 'funding', 'leverage', 'your ', 'you ', 'risk', 'expected move']

/** Prose paragraphs of a markdown answer: no tables, headings or code. */
export function proseParagraphs(markdown: string): string[] {
  return markdown
    .split(/\n\s*\n/)
    .map((block) => block.trim())
    .filter(
      (block) =>
        block.length > 0 &&
        !block.startsWith('#') &&
        !block.startsWith('```') &&
        !block.split('\n').some((line) => line.trim().startsWith('|'))
    )
}

/** The first sentences of a text, citations removed, within a character budget. */
export function summarize(text: string, maxSentences = 3, maxChars = 420): string {
  const clean = text.replace(CITATION, '').replace(/\s+/g, ' ').trim()
  // Split only where a stop is followed by a space, so "2706.425" stays one number.
  const sentences = clean.split(/(?<=[.!?])\s+/)
  let out = ''
  let count = 0
  for (const sentence of sentences) {
    const next = sentence.trim()
    if (out && out.length + next.length + 1 > maxChars) break
    out = out ? `${out} ${next}` : next
    count += 1
    if (count >= maxSentences) break
  }
  return out
}

function relevance(paragraph: string, terms: readonly string[]): number {
  const text = paragraph.toLowerCase()
  return [...terms, ...RELEVANT].filter((term) => term && text.includes(term.toLowerCase())).length
}

/**
 * Reads the brief back. When the answer kept to "## Brief" / "## Idea N", each part goes where
 * it belongs. The research engine often answers with a full report instead (tables, citations,
 * many sections); then the brief is the few sentences most about the user's book — the
 * paragraphs naming their symbols, liquidation, funding — and the whole report stays readable
 * behind "Read the full brief".
 */
export function parseBrief(markdown: string, terms: readonly string[] = []): ParsedBrief {
  const text = markdown.trim()
  const marks = [...text.matchAll(HEADING)]
  if (marks.length > 0) {
    const ideas: Record<number, string> = {}
    const parts: Record<string, string> = {}
    marks.forEach((mark, index) => {
      const start = (mark.index ?? 0) + mark[0].length
      const end = marks[index + 1]?.index ?? text.length
      const body = text.slice(start, end).trim().replace(CITATION, '')
      if (mark[2]) ideas[Number(mark[2])] = body
      else parts[mark[1].toLowerCase()] = body
    })
    // Anything written before the first heading still belongs to the brief.
    const lead = text
      .slice(0, marks[0].index ?? 0)
      .trim()
      .replace(CITATION, '')
    const sections =
      parts['what changed'] || parts['what it means for you'] || parts['what to watch']
        ? {
            changed: parts['what changed'] ?? '',
            meaning: parts['what it means for you'] ?? '',
            watch: parts['what to watch'] ?? '',
          }
        : null
    const brief = sections
      ? [sections.changed, sections.meaning, sections.watch].filter(Boolean).join(' ')
      : [lead, parts.brief].filter(Boolean).join('\n\n')
    return {
      brief,
      sections,
      ideas,
      cardNotes: parts.cards ? parseCardNotes(parts.cards) : {},
      full: text,
      structured: true,
    }
  }

  const paragraphs = proseParagraphs(text)
  const ranked = paragraphs
    .map((paragraph, index) => ({ paragraph, index, score: relevance(paragraph, terms) }))
    .sort((a, b) => b.score - a.score || a.index - b.index)
    .slice(0, 2)
    .sort((a, b) => a.index - b.index)
  const brief = summarize(ranked.map((entry) => entry.paragraph).join(' ') || text)
  return { brief, sections: null, ideas: {}, cardNotes: {}, full: text, structured: false }
}

/** From an unstructured answer, the sentences about one idea: the paragraph naming its terms. */
export function findReasoning(markdown: string, terms: readonly string[]): string | null {
  const hit = proseParagraphs(markdown).find((paragraph) =>
    terms.some((term) => term && paragraph.toLowerCase().includes(term.toLowerCase()))
  )
  return hit ? summarize(hit, 2, 300) : null
}
